/**
 * @file Pure chat logic — zero DOM dependency.
 *
 * During Vue migration:
 *   - relativeTime    → utils/time.ts or date-fns
 *   - getChatExamples → useChatStore().examples (computed)
 *   - scopeMeta       → ChatMessage.vue component logic
 *   - lastAiIndex     → useChatStore().lastAiIndex (computed)
 *   - formatChatEntry → ChatMessage.vue template
 *   - SUGGESTION_CATEGORIES → useSuggestions() composable
 */
import type { ChatMessage, Section } from '../types.ts';

// ── TIME FORMATTING ────────────────────────────────────────────────

/**
 * Format a timestamp as a human-readable relative time string.
 * e.g. "just now", "5m ago", "2h ago", "Mar 15"
 */
export const relativeTime = (ts: number): string => {
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 10) {
    return 'just now';
  }
  if (diff < 60) {
    return `${diff}s ago`;
  }
  if (diff < 3600) {
    return `${Math.floor(diff / 60)}m ago`;
  }
  if (diff < 86_400) {
    return `${Math.floor(diff / 3600)}h ago`;
  }
  return new Date(ts).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
};

// ── CONTEXTUAL EXAMPLES ────────────────────────────────────────────

/**
 * Generate contextual chat examples based on which sections are built.
 * Returns up to 4 relevant example prompts.
 */
export const getChatExamples = (builtSections: Section[]): string[] => {
  const types = new Set(builtSections.map(s => s.type));
  const examples: string[] = [];

  if (types.has('hero')) {
    examples.push('"make the hero section more dramatic"');
  }
  if (types.has('features') || types.has('services')) {
    examples.push('"change to a card grid layout"');
  }
  if (!(types.has('testimonials') || types.has('social-proof'))) {
    examples.push('"add a testimonials section"');
  }
  if (builtSections.length >= 3) {
    examples.push('"add more whitespace between sections"');
  }
  examples.push('"change accent color to orange"');

  return examples.slice(0, 4);
};

// ── SCOPE METADATA ─────────────────────────────────────────────────

export interface ScopeMeta {
  icon: string;
  label: string;
  cls: string;
}

/** Map AI response scope to display metadata. */
export const getScopeMeta = (scope: string | undefined): ScopeMeta => {
  const map: Record<string, ScopeMeta> = {
    clarify: { icon: '?', label: 'Question', cls: 'scope-clarify' },
    tokens: { icon: '\u25cf', label: 'tokens', cls: 'scope-tokens' },
    vision: { icon: '\u25c6', label: 'vision', cls: 'scope-vision' },
    global: { icon: '\u2b24', label: 'global', cls: 'scope-global' },
    add: { icon: '\uff0b', label: 'added', cls: 'scope-add' },
    remove: { icon: '\u2212', label: 'removed', cls: 'scope-remove' },
  };
  return map[scope || ''] || { icon: '\u2713', label: scope || '', cls: 'scope-section' };
};

/** Non-section scopes (don't map to iframe elements). */
const NON_SECTION_SCOPES = new Set(['clarify', 'tokens', 'vision', 'global', 'add', 'remove']);

/**
 * Extract section IDs from a scope string.
 * Returns empty array for non-section scopes.
 */
export const getScopeIds = (scope: string | undefined): string[] => {
  if (!scope || NON_SECTION_SCOPES.has(scope)) {
    return [];
  }
  return scope.split(',');
};

// ── CHAT LOG HELPERS ───────────────────────────────────────────────

/**
 * Find the index of the last AI message in a chat log.
 * Returns -1 if no AI messages exist.
 */
export const lastAiIndex = (log: ChatMessage[]): number => {
  for (let i = log.length - 1; i >= 0; i--) {
    if (log[i].role === 'ai') {
      return i;
    }
  }
  return -1;
};

// ── SUGGESTION CATEGORIES ──────────────────────────────────────────

/** Color mapping for suggestion categories. */
export const SUGGESTION_CATEGORY_COLORS: Record<string, string> = {
  conversion: '#10b981',
  design: '#6366f1',
  content: '#f59e0b',
  ux: '#3b82f6',
  accessibility: '#8b5cf6',
};
